<script setup lang="ts">
import { computed } from "vue";
import { runtimeConfig } from "../api/config";
import type { resolveRoute } from "../routes";

const props = defineProps<{ route: ReturnType<typeof resolveRoute> }>();
type LegalDocumentName = "privacy" | "terms" | "refund";

const kind = computed<LegalDocumentName>(() => props.route.name === "legal" ? "privacy" : props.route.name as LegalDocumentName);
const legal = runtimeConfig.legalIdentity;
const contactHref = computed(() => legal.contactEmail ? `mailto:${legal.contactEmail}` : null);
const phoneHref = computed(() => legal.contactPhone ? `tel:${legal.contactPhone.replace(/[^+\d]/g, "")}` : null);

const documents = [
  { name: "privacy", href: "/privacy/", label: "隐私政策" },
  { name: "terms", href: "/terms/", label: "用户协议" },
  { name: "refund", href: "/refund-policy/", label: "退款政策" },
] as const;

const chapters: Record<LegalDocumentName, ReadonlyArray<{ href: string; label: string }>> = {
  privacy: [
    { href: "#privacy-scope", label: "适用范围" },
    { href: "#privacy-data", label: "信息清单" },
    { href: "#privacy-permissions", label: "扩展权限" },
    { href: "#privacy-sharing", label: "共享与委托" },
    { href: "#privacy-rights", label: "你的权利" },
    { href: "#privacy-contact", label: "联系方式" },
  ],
  terms: [
    { href: "#terms-service", label: "服务内容" },
    { href: "#terms-local", label: "本地处理" },
    { href: "#terms-rules", label: "使用规则" },
    { href: "#terms-liability", label: "责任边界" },
  ],
  refund: [
    { href: "#refund-apply", label: "申请方式" },
    { href: "#refund-full", label: "全额退款" },
    { href: "#refund-partial", label: "部分退款" },
    { href: "#refund-status", label: "处理状态" },
  ],
};

function handleChapterClick(event: MouseEvent, href: string): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const section = document.querySelector<HTMLElement>(href);
  if (!section) return;
  event.preventDefault();
  const headerHeight = document.querySelector<HTMLElement>(".site-header")?.getBoundingClientRect().height ?? 0;
  const targetTop = Math.max(0, window.scrollY + section.getBoundingClientRect().top - headerHeight - 24);
  window.history.pushState({}, "", `${window.location.pathname}${href}`);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: targetTop, behavior: reducedMotion ? "auto" : "smooth" });
}

const privacyRows = [
  ["1688 商品与素材", "当前商品页链接、商品编号、标题、供应商、属性、可验证的 SKU、价格和物流信息；主图、商品视频、SKU 图、详情图的链接、类型与顺序。", "识别、分类、预览、整理并下载你正在查看的商品素材。插件只处理当前受支持的商品页，不收集一般浏览历史。", "商品缓存保存在浏览器扩展本地；媒体内容只在预览与下载期间使用，不发送给图萃服务器或第三方模型。"],
  ["选择与下载", "素材选择状态、检测结果、ZIP 分包进度和本地下载结果。", "记住当前选择并在浏览器内生成 ZIP。", "选择状态保存在扩展本地；媒体临时内容会在取消、关闭或完成后释放。"],
] as const;

const permissionRows = [
  ["activeTab", "用户打开插件后，临时读取当前标签页并确认它是受支持的 1688 商品页。"],
  ["scripting", "把仓库内置采集器注入当前受支持页面；不向任意网站注入。"],
  ["storage", "保存界面字号、商品缓存和素材选择状态。"],
  ["downloads", "把扩展本地生成的 ZIP 交给 Chrome 下载。"],
  ["受限 host_permissions", "访问 1688 与阿里素材来源；不申请访问所有网站。"],
] as const;
</script>

<template>
  <div class="legal-center page-width">
    <aside class="legal-center__sidebar" aria-label="隐私与协议中心">
      <strong class="legal-center__title">隐私与协议</strong>
      <nav class="legal-document-nav" aria-label="法律文档">
        <a v-for="document in documents" :key="document.name" :href="document.href" data-route :aria-current="kind === document.name ? 'page' : undefined">{{ document.label }}</a>
      </nav>
      <nav class="legal-chapter-nav" :aria-label="`${documents.find((document) => document.name === kind)?.label}章节`">
        <strong>本页目录</strong>
        <a v-for="chapter in chapters[kind]" :key="chapter.href" :href="chapter.href" @click="handleChapterClick($event, chapter.href)">{{ chapter.label }}</a>
      </nav>
    </aside>
    <article class="legal-page">
    <header v-if="kind === 'privacy'">
      <h1>隐私政策</h1>
      <aside><strong>关键摘要：</strong>素材识别、选择和 ZIP 下载在浏览器本地完成。当前版本不需要登录，不提供在线分析、点数或支付功能，也不会把商品素材发送给图萃服务器或第三方模型。</aside>
    </header>
    <header v-else-if="kind === 'terms'">
      <h1>用户协议</h1>
      <aside>本协议说明图萃当前免费本地工具提供的服务、使用规则和双方责任。</aside>
    </header>
    <header v-else>
      <h1>退款政策</h1>
      <aside>当前版本免费提供，不创建订单或收取费用，因此没有可申请退款的交易。</aside>
    </header>

    <template v-if="kind === 'privacy'">
      <section id="privacy-scope"><h2>1. 适用范围、定义与运营者</h2><p>本政策适用于图萃官网和浏览器扩展。1688、Chrome/Chromium 及其他第三方独立提供的服务适用其各自规则。</p><p>图萃的运营显示名为<strong>广州琴景网络工作室</strong>；已确认的联系信息集中列在本政策“运营主体与联系”章节。</p></section>
      <section><h2>2. 处理原则与依据</h2><p>我们按合法、正当、必要、诚信和最小范围处理信息。当前版本只为完成用户主动请求的素材识别、选择和本地下载读取必要页面信息。</p><p>如果未来重新引入账号、在线分析、第三方模型或付费能力，我们会先更新产品界面和政策说明，不会在未披露的情况下沿用当前授权。</p></section>
      <section id="privacy-data"><h2>3. 我们处理的信息</h2><div class="legal-data-list"><article v-for="row in privacyRows" :key="row[0]"><h3>{{ row[0] }}</h3><dl><div><dt>具体信息</dt><dd>{{ row[1] }}</dd></div><div><dt>目的与依据</dt><dd>{{ row[2] }}</dd></div><div><dt>位置与期限</dt><dd>{{ row[3] }}</dd></div></dl></article></div></section>
      <section id="privacy-permissions"><h2>4. Chrome 扩展权限</h2><dl class="permission-list"><div v-for="row in permissionRows" :key="row[0]"><dt><code>{{ row[0] }}</code></dt><dd>{{ row[1] }}</dd></div></dl><p>扩展本地存储不是加密保险箱。请保护设备与浏览器账户；清除扩展数据会同时清除本地偏好、商品缓存和选择状态。</p></section>
      <section id="privacy-sharing"><h2>5. 第三方与数据提供</h2><dl><div><dt>Chrome / Chromium</dt><dd>提供扩展安装、权限、本地存储和下载能力。</dd></div><div><dt>1688 与阿里素材域名</dt><dd>作为你主动访问的商品与媒体来源，只在识别、预览或下载当前商品素材时访问。</dd></div></dl><p>图萃不会把商品素材提供给图萃服务器、第三方模型、广告平台、数据经纪商或信息转售方。</p><p><strong>Chrome Limited Use：</strong>通过扩展权限取得的信息只用于当前商品素材的识别、整理和下载，不用于个性化、重定向、兴趣广告或信用评估。</p></section>
      <section><h2>6. 保存、删除与安全</h2><p>界面偏好、商品缓存和素材选择状态保存在浏览器扩展本地。媒体字节只在预览和下载期间临时处理，完成、取消或关闭扩展后释放。</p><p>图萃限制可访问域名，并对下载媒体的类型、大小、超时和失败进行处理。没有系统能保证绝对安全，请只在可信设备和浏览器环境中使用。</p></section>
      <section id="privacy-rights"><h2>7. 你的权利与选择</h2><p>你可以逐项选择或取消素材、取消未完成下载，并通过浏览器清除扩展数据或移除扩展。</p><p>如果对信息处理方式有疑问，可以通过本政策公布的联系渠道提出请求。</p></section>
      <section><h2>8. 未成年人</h2><p>图萃面向商品素材整理场景，不专门面向未成年人。未达到所在地法律规定的独立同意年龄时，应由监护人同意并指导使用。</p></section>
      <section><h2>9. 政策更新</h2><p>重大处理变化会通过官网、扩展内提示或其他显著方式告知；依法需要重新同意的将重新取得。</p></section>
    </template>

    <template v-else-if="kind === 'terms'">
      <section><h2>1. 协议接受与定义</h2><p>当你使用图萃扩展时，即表示你理解并接受本协议。“素材”包括当前 1688 商品页的商品信息、图片和商品视频。</p></section>
      <section id="terms-service"><h2>2. 服务内容与真实边界</h2><p>图萃提供当前受支持 1688 商品页的素材检测、分类、选择、预览、商品信息整理和本地 ZIP 下载。当前版本免费提供，不需要账号，不创建点数或订单，也不处理支付。</p><p>网页结构、素材清晰度、登录状态和网络情况会影响识别与下载结果。你必须在下载、发布或商业使用前人工复核素材和商品信息。</p></section>
      <section id="terms-local"><h2>3. 本地处理</h2><p>素材识别、选择和 ZIP 打包主要在浏览器本地完成。下载时，扩展会访问当前 1688 商品页引用的受支持素材域名，但不会把商品素材发送给图萃服务器或第三方模型。</p><p>清除浏览器扩展数据或移除扩展会清除本地偏好、商品缓存和选择状态；未完成的下载可能无法恢复。</p></section>
      <section><h2>4. 素材权利与第三方平台规则</h2><p>你应确认对采集、下载、编辑、发布或商业使用的素材拥有合法权利或授权，并遵守知识产权、个人信息、广告、电商和消费者保护规则，以及 1688 与目标发布平台规则。图萃提供技术整理工具，不转让素材权利，也不保证第三方素材可自由使用。</p><p>不得利用图萃绕过登录、访问控制、付费墙、反爬或平台安全措施，不得批量侵权、抓取与当前功能无关的数据，或将商品/联系人信息用于骚扰和非法营销。</p></section>
      <section id="terms-rules"><h2>5. 使用规则</h2><ul><li>不得攻击、探测、逆向或绕过浏览器、1688 或图萃的安全控制。</li><li>不得利用图萃实施侵权、欺诈、虚假宣传、非法交易、骚扰或其他违法活动。</li><li>不得冒用图萃、1688 或第三方身份、商标和认可。</li></ul></section>
      <section><h2>6. 知识产权</h2><p>图萃的软件、代码、界面、品牌和文档权利归相应权利人所有。除法律允许或另有书面许可外，不得复制、出租、出售、移除权利标识或以图萃名义发布衍生服务。用户及第三方素材权利不因使用图萃而转移。</p></section>
      <section><h2>7. 服务变更、中断与终止</h2><p>1688 页面结构、浏览器政策、素材源和网络可能导致功能变化或中断。图萃会在合理范围维护并提供明确失败状态，但不承诺绝不中断或绝对准确。</p></section>
      <section id="terms-liability"><h2>8. 免责声明与责任限制</h2><p>用户应对素材权利、人工复核和最终发布负责。图萃不对第三方页面内容、素材合法性或页面持续可访问性作保证。法律不得排除或限制的消费者权益仍依法适用。</p></section>
      <section><h2>9. 法律适用、争议与更新</h2><p>本协议适用中华人民共和国大陆地区法律。发生争议时，双方应先协商处理；协商不成的，依适用法律确定调解、诉讼机构和管辖。</p><p>依法影响用户重要权益的变化将通过官网或产品内提示显著告知。</p></section>
    </template>

    <template v-else>
      <section id="refund-apply"><h2>1. 当前版本</h2><p>图萃当前版本免费提供，不创建订单、不销售点数，也不收取软件服务费，因此不存在可申请退款的交易。</p></section>
      <section id="refund-full"><h2>2. 后续变化</h2><p>如果未来重新提供付费能力，图萃会在正式收费前公布适用的价格、支付方式、履约内容和退款规则，不会用本页面为尚未开放的收费行为提供依据。</p></section>
      <section id="refund-partial"><h2>3. 异常付款</h2><p>如果你发现有人以图萃名义要求个人转账、支付密码、完整银行卡号或未经官网确认的费用，请不要付款，并通过隐私政策公布的联系渠道核实。</p></section>
      <section id="refund-status"><h2>4. 联系</h2><p>运营主体与联系渠道见<a href="/privacy#privacy-contact">《隐私政策》“运营主体与联系”章节</a>。</p></section>
    </template>

    <section v-if="kind === 'privacy'" id="privacy-contact" class="legal-identity" aria-labelledby="legal-identity-title"><h2 id="legal-identity-title">10. 运营主体与联系</h2><dl><div><dt>运营主体</dt><dd>{{ legal.entityName ?? "广州琴景网络工作室" }}</dd></div><div v-if="phoneHref"><dt>客服电话</dt><dd><a :href="phoneHref">{{ legal.contactPhone }}</a></dd></div><div v-if="contactHref"><dt>联系邮箱</dt><dd><a :href="contactHref">{{ legal.contactEmail }}</a></dd></div><div><dt>ICP备案</dt><dd><a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">粤ICP备2024241383号-1</a></dd></div></dl></section>
    <section v-else class="legal-contact-reference"><h2>联系</h2><p>运营主体与联系渠道见<a href="/privacy#privacy-contact">《隐私政策》“运营主体与联系”章节</a>。</p></section>
    <footer class="legal-footer">本文件根据图萃当前提供的服务和适用规则编写，不替代专业法律意见。服务或规则发生重大变化时，我们会通过官网或产品内提示告知。</footer>
    </article>
  </div>
</template>
